/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import defaultsDeep from 'lodash/defaultsDeep';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import { maybeWaitForPremiumModal } from '../utils.tsx';
import type { Props, State } from '../../types.tsx';
import { BLUEPRINTS_TOUR_SPOTLIGHTS } from '../../constants.tsx';
import { normalizeError } from '../../../common/utils.tsx';

type RemoteState = {
	tourDismissed: boolean;
};

export const loadBlueprintsState =
	(): Action<State, Props, Promise<void>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().blueprints.tourDismissed === 'boolean') {
			return;
		}

		try {
			const remoteState = await getUserProperty<RemoteState>(accountId, BLUEPRINTS_TOUR_SPOTLIGHTS);

			const state = getState();

			setState({
				...state,
				blueprints: {
					...state.blueprints,
					...defaultsDeep(remoteState, {
						tourDismissed: false,
					}),
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					blueprints: {
						...state.blueprints,
						tourDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.blueprints-spotlight',
				`Failed to get user property with key: ${BLUEPRINTS_TOUR_SPOTLIGHTS}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};

export const setBlueprintsTourDismissed =
	(tourDismissed: boolean): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newBlueprintsState = {
			...state.blueprints,
			tourDismissed,
		};

		setState({
			...state,
			blueprints: newBlueprintsState,
		});

		try {
			await setUserProperties(
				accountId,
				BLUEPRINTS_TOUR_SPOTLIGHTS,
				JSON.stringify(newBlueprintsState),
			);
		} catch (error) {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.onboarding.blueprints-spotlight', normalizeError(error)),
			);
		}
	};
